import { useCallback, useMemo, useRef, useState } from "react";
import type { BoutiqueRefDTO } from "@easyshop/shared";
import { cx } from "@/components/ui";
import { useClickOutside } from "@/hooks/useClickOutside";

function normalize(s: string) {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Liste déroulante de boutiques avec recherche. `multiple` : cases à cocher
 * (plusieurs boutiques) ; sinon un seul choix, qui referme la liste.
 */
export function BoutiquePicker({
  boutiques,
  value,
  onChange,
  multiple = false,
  placeholder = "Choisir une boutique…",
}: {
  boutiques: BoutiqueRefDTO[];
  value: string[];
  onChange: (ids: string[]) => void;
  multiple?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(ref, close, open);

  const filtered = useMemo(() => {
    const needle = normalize(search.trim());
    return needle ? boutiques.filter((b) => normalize(`${b.city} ${b.name}`).includes(needle)) : boutiques;
  }, [boutiques, search]);

  const selected = boutiques.filter((b) => value.includes(b.id));
  const label =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? selected[0].city
        : `${selected.length} boutiques : ${selected
            .slice(0, 3)
            .map((b) => b.city)
            .join(", ")}${selected.length > 3 ? "…" : ""}`;

  const pick = (id: string) => {
    if (!multiple) {
      onChange([id]);
      setOpen(false);
      setSearch("");
      return;
    }
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cx(
          "flex w-full items-center gap-2 rounded-[10px] border bg-white px-3 py-[10px] text-left text-[13px]",
          open ? "border-accent" : "border-line"
        )}
      >
        <span className={cx("min-w-0 flex-1 truncate", selected.length ? "font-semibold" : "text-muted-2")}>{label}</span>
        <span aria-hidden className="text-[11px] text-muted-2">
          ▾
        </span>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 overflow-hidden rounded-[12px] border border-line bg-white shadow-menu">
          <div className="border-b border-line-soft p-2">
            <input
              autoFocus
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une boutique…"
              className="w-full rounded-[8px] bg-page px-3 py-2 text-[12.5px] outline-none"
            />
            {multiple && (
              <div className="mt-2 flex gap-3 px-1 text-[11.5px] font-semibold">
                <button type="button" className="text-accent" onClick={() => onChange([...new Set([...value, ...filtered.map((b) => b.id)])])}>
                  Tout cocher{search ? " (filtrées)" : ""}
                </button>
                <button type="button" className="text-muted-2 hover:text-ink" onClick={() => onChange(search ? value.filter((id) => !filtered.some((b) => b.id === id)) : [])}>
                  Tout décocher
                </button>
              </div>
            )}
          </div>
          <div role="listbox" aria-multiselectable={multiple} className="max-h-[260px] overflow-auto py-1">
            {filtered.length === 0 && <div className="px-4 py-3 text-[12.5px] text-muted">Aucune boutique trouvée.</div>}
            {filtered.map((b) => {
              const on = value.includes(b.id);
              return (
                <button
                  key={b.id}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => pick(b.id)}
                  className={cx("flex w-full items-center gap-[10px] px-4 py-[9px] text-left text-[13px] hover:bg-page", on && !multiple && "bg-accent-row")}
                >
                  {multiple && (
                    <span
                      aria-hidden
                      className={cx(
                        "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[4px] border text-[10px] font-bold",
                        on ? "border-accent bg-accent text-white" : "border-line bg-white"
                      )}
                    >
                      {on ? "✓" : ""}
                    </span>
                  )}
                  <span className={cx("h-2 w-2 flex-shrink-0 rounded-full", b.type === "INTEGRATED" ? "bg-positive" : "bg-franchise")} />
                  <span className={cx("min-w-0 flex-1 truncate", on && "font-semibold")}>{b.city}</span>
                  {b.name !== b.city && <span className="max-w-[45%] flex-shrink-0 truncate text-[11px] text-faint">{b.name}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {multiple && selected.length > 0 && (
        <div className="mt-2 flex max-h-[84px] flex-wrap gap-[6px] overflow-auto">
          {selected.map((b) => (
            <span
              key={b.id}
              className={cx(
                "inline-flex items-center gap-1 rounded-full px-[9px] py-[3px] text-[11px] font-semibold",
                b.type === "INTEGRATED" ? "bg-positive-bg text-positive" : "bg-franchise-bg text-franchise"
              )}
            >
              {b.city}
              <button type="button" aria-label={`Retirer ${b.city}`} onClick={() => pick(b.id)} className="opacity-70 hover:opacity-100">
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
